import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed } from 'vue'
import { doc, collection, query, onSnapshot, getDocs, orderBy, limit, Unsubscribe } from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import { normFull, type RegWatchMatchEntry } from '@/utils/regulationWatch'
import { RegulationReport } from '@/models/RegulationReport'
import { ReportEntry } from '@/models/ReportEntry'
import { RegulationListItem } from '@/models/RegulationListItem'
import { Assessment } from '@/models/Assessment'
import { Task } from '@/models/Task'
import { TDExtraction, TDAppliedStandards, TDDeviceAttributes, tdSectionGroups, tdSubsectionByKey } from '@/models/td'
import type { TDSectionSummary, TDRegulation, TDSectionStatus, TDRatings } from '@/models/td'
import { useProjectsStore } from '@/stores/projects'
import { useTasksStore } from '@/stores/tasks'
import { isAcceptedKanbanTask } from '@/utils/taskSourceOfTruth'
import { logger } from '@/utils/logger'

export interface SectionEntry {
  key: string
  groupKey: string
  section_title: string
  annex_ref?: string
  status: TDSectionStatus
  overall_rating?: string
  rating_counts?: TDRatings
  proposed_tasks_counts?: { A: number; B: number; C: number; D: number; TOTAL: number }
  last_run_at?: Date
  last_error?: string
  latest_assessment_id?: string
}

const TD_LATEST_ID = 'td_latest'

export const useTDCurrentStore = defineStore('tdCurrent', () => {
  const current = ref<Assessment | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const _rawTasks = ref<Task[]>([])
  // MYAC-719: accepted tasks are owned by the kanban ProjectTask — swap them in
  // so the TD view shows and edits the kanban copy (source td_latest is frozen).
  // Writable so callers/tests can still assign the raw task list directly.
  const allTasks = computed<Task[]>({
    get() {
      const kanban = useTasksStore()
      // Read the plain `allAcceptedTasks` getter (not the parametrized `taskById`)
      // so this computed reactively re-runs when the kanban store resolves tasks.
      const byId = new Map(kanban.allAcceptedTasks.map((k) => [k.id, k]))
      return _rawTasks.value.map((t) => (isAcceptedKanbanTask(t) ? (byId.get(t.id) ?? t) : t))
    },
    set(value) {
      _rawTasks.value = value
    },
  })
  const allExtractions = ref<TDExtraction[]>([])
  const appliedStandards = ref<TDAppliedStandards | null>(null)
  const deviceAttributes = ref<TDDeviceAttributes | null>(null)

  // MYAC-822 — regulation-watch data resolved LIVE against the tenant's latest
  // report on each Applied Standards tab activation. The frozen labels stamped
  // on applied_standards/master by the TD job are ignored by the tab; these
  // inputs + the utils/regulationWatch matcher produce the displayed labels.
  interface RegWatchLive {
    entries: RegWatchMatchEntry[]
    monitoredFulls: string[]
    referenceDate: string | null
    generatedDate: string | null
  }
  const regWatchLive = ref<RegWatchLive | null>(null)
  const regWatchLiveLoading = ref(false)
  const regWatchLiveError = ref(false)
  // Generation counter: overlapping resolves (rapid tab switches) race, and the
  // slower response must not overwrite the newer one — only the latest call may
  // commit its result.
  let _regWatchSeq = 0

  let _unsub: Unsubscribe | null = null
  let _tasksUnsub: Unsubscribe | null = null
  let _extractionsUnsub: Unsubscribe | null = null
  let _appliedStandardsUnsub: Unsubscribe | null = null
  let _deviceAttributesUnsub: Unsubscribe | null = null
  let _currentPid: string | null = null

  const projectsStore = useProjectsStore()
  const regulation = computed<TDRegulation>(() => projectsStore.currentProject?.td_regulation ?? 'mdr')
  const isContinuityApplicable = computed(() => !!projectsStore.currentProject?.market_placement_date)

  function setCurrentProject(pid: string | null) {
    // Kanban tasks back the accepted-task overlay (allTasks). Ensure they're
    // (re)loaded on every entry: the kanban board tears the shared store down on
    // leave (TasksPage → unsubscribeAll), and the guard below would otherwise
    // skip the reload when returning to the same TD project. loadKanbanTasks is
    // idempotent (early-returns if still loaded).
    if (pid) void useTasksStore().loadKanbanTasks(pid)
    if (_currentPid === pid) return
    _unsubscribe()
    _currentPid = pid
    if (pid) _subscribe(pid)
  }

  function _subscribe(pid: string) {
    loading.value = true
    error.value = null
    const fs = useFirestore()
    const docRef = doc(fs, `projects/${pid}/assessments/${TD_LATEST_ID}`)
    _unsub = onSnapshot(
      docRef,
      (snap) => {
        current.value = snap.exists() ? Assessment.fromFirebase(snap, null) : null
        loading.value = false
      },
      (err) => {
        logger.error('td_latest subscription error', err)
        error.value = err.message
        loading.value = false
      },
    )
    const tasksQ = query(collection(fs, `projects/${pid}/assessments/${TD_LATEST_ID}/tasks`))
    _tasksUnsub = onSnapshot(
      tasksQ,
      (snap) => {
        _rawTasks.value = snap.docs
          .map((d) => Task.fromFirebase(d))
          .filter((t): t is Task => t !== null)
      },
      (err) => {
        logger.error('TD tasks subscription error', err)
      },
    )
    const extractionsQ = query(collection(fs, `projects/${pid}/assessments/${TD_LATEST_ID}/extractions`))
    _extractionsUnsub = onSnapshot(
      extractionsQ,
      (snap) => {
        allExtractions.value = snap.docs
          .map((d) => TDExtraction.fromFirebase(d))
          .filter((e): e is TDExtraction => e !== null)
      },
      (err) => {
        logger.error('TD extractions subscription error', err)
      },
    )
    const appliedStandardsRef = doc(
      fs, `projects/${pid}/assessments/${TD_LATEST_ID}/applied_standards/master`,
    )
    _appliedStandardsUnsub = onSnapshot(
      appliedStandardsRef,
      (snap) => {
        appliedStandards.value = snap.exists() ? TDAppliedStandards.fromFirebase(snap) : null
      },
      (err) => {
        logger.error('TD applied_standards subscription error', err)
      },
    )
    const deviceAttributesRef = doc(
      fs, `projects/${pid}/assessments/${TD_LATEST_ID}/device_attributes/master`,
    )
    _deviceAttributesUnsub = onSnapshot(
      deviceAttributesRef,
      (snap) => {
        deviceAttributes.value = snap.exists() ? TDDeviceAttributes.fromFirebase(snap) : null
      },
      (err) => {
        logger.error('TD device_attributes subscription error', err)
      },
    )
  }

  function _unsubscribe() {
    if (_unsub) { _unsub(); _unsub = null }
    if (_tasksUnsub) { _tasksUnsub(); _tasksUnsub = null }
    if (_extractionsUnsub) { _extractionsUnsub(); _extractionsUnsub = null }
    if (_appliedStandardsUnsub) { _appliedStandardsUnsub(); _appliedStandardsUnsub = null }
    if (_deviceAttributesUnsub) { _deviceAttributesUnsub(); _deviceAttributesUnsub = null }
    current.value = null
    _rawTasks.value = []
    allExtractions.value = []
    appliedStandards.value = null
    deviceAttributes.value = null
    error.value = null
  }

  function clear() { setCurrentProject(null) }

  function retry() {
    const pid = _currentPid
    _unsubscribe()
    _currentPid = null
    setCurrentProject(pid)
  }

  const sections = computed<Record<string, TDSectionSummary>>(
    () => current.value?.td_sections ?? {}
  )
  const sectionEntries = computed<SectionEntry[]>(() => {
    const raw = sections.value
    const groups = tdSectionGroups(regulation.value)
    const entries: SectionEntry[] = []

    for (const group of groups) {
      const subsInData = group.subsections.filter((sub) => sub.key in raw)
      if (subsInData.length === 0) continue

      for (const sub of subsInData) {
        const section = raw[sub.key]
        entries.push({
          key: sub.key,
          groupKey: group.groupKey,
          section_title: sub.title,
          annex_ref: group.annex_ref,
          status: section.status,
          overall_rating: section.overall_rating,
          rating_counts: section.rating_counts,
          proposed_tasks_counts: section.proposed_tasks_counts,
          last_run_at: section.last_run_at,
          last_error: section.last_error,
          latest_assessment_id: section.latest_assessment_id,
        })
      }
    }
    return entries
  })
  const groupLevelEntries = computed<SectionEntry[]>(() => {
    const raw = sections.value
    const groups = tdSectionGroups(regulation.value)
    const entries: SectionEntry[] = []

    for (const group of groups) {
      const subsInData = group.subsections.filter((sub) => sub.key in raw)
      if (subsInData.length === 0) continue

      const first = raw[subsInData[0].key]
      const worstStatus = subsInData.reduce<TDSectionStatus>((worst, sub) => {
        const s = raw[sub.key]?.status ?? 'pending'
        if (s === 'failed' || worst === 'failed') return 'failed'
        if (s === 'running' || worst === 'running') return 'running'
        if (s === 'pending' || worst === 'pending') return 'pending'
        return s
      }, 'done' as TDSectionStatus)

      entries.push({
        key: subsInData[0].key,
        groupKey: group.groupKey,
        section_title: group.groupLabel,
        annex_ref: group.annex_ref,
        status: worstStatus,
        overall_rating: first.overall_rating,
        rating_counts: first.rating_counts,
        proposed_tasks_counts: first.proposed_tasks_counts,
        last_run_at: first.last_run_at,
        last_error: first.last_error,
        latest_assessment_id: first.latest_assessment_id,
      })
    }
    return entries
  })

  const extractionEntries = computed<SectionEntry[]>(() => {
    const raw = sections.value
    const groups = tdSectionGroups(regulation.value)
    const entries: SectionEntry[] = []

    for (const group of groups) {
      const firstSub = group.subsections.find((s) => s.key in raw)
      if (!firstSub) continue

      for (const ext of group.extractions) {
        const sectionData = raw[ext.key] ?? raw[firstSub.key]

        entries.push({
          key: ext.key,
          groupKey: group.groupKey,
          section_title: ext.title,
          annex_ref: group.annex_ref,
          status: sectionData.status,
          overall_rating: sectionData.overall_rating,
          rating_counts: sectionData.rating_counts,
          proposed_tasks_counts: sectionData.proposed_tasks_counts,
          last_run_at: sectionData.last_run_at,
          last_error: sectionData.last_error,
          latest_assessment_id: sectionData.latest_assessment_id,
        })
      }
    }
    return entries
  })

  const consistencyEntries = computed<SectionEntry[]>(() => {
    const raw = sections.value
    const groups = tdSectionGroups(regulation.value)
    const entries: SectionEntry[] = []

    for (const group of groups) {
      const firstSub = group.subsections.find((s) => s.key in raw)
      if (!firstSub) continue

      for (const ext of group.extractions) {
        if (consistencyTaskCountForSection(ext.key) === 0) continue
        const sectionData = raw[ext.key] ?? raw[firstSub.key]

        entries.push({
          key: ext.key,
          groupKey: group.groupKey,
          section_title: ext.title,
          annex_ref: group.annex_ref,
          status: sectionData.status,
          overall_rating: sectionData.overall_rating,
          rating_counts: sectionData.rating_counts,
          proposed_tasks_counts: sectionData.proposed_tasks_counts,
          last_run_at: sectionData.last_run_at,
          last_error: sectionData.last_error,
          latest_assessment_id: sectionData.latest_assessment_id,
        })
      }
    }
    return entries
  })

  const macScore = computed(() => current.value?.mac_score ?? null)
  const macScorePrevious = computed(() => current.value?.mac_score_previous ?? null)
  const macScoreProgression = computed(() => current.value?.mac_score_progression ?? 'stable')
  const overallRating = computed(() => current.value?.overall_rating ?? null)
  const overallRatingCounts = computed(() => current.value?.overall_rating_counts ?? null)
  const aggregateTaskCounts = computed(() => current.value?.aggregate_proposed_tasks_counts ?? null)
  const completenessCounts = computed(() => current.value?.completeness_counts ?? null)
  const consistencyCounts = computed(() => current.value?.consistency_counts ?? null)
  const complianceTasksCount = computed(() => current.value?.compliance_tasks_count ?? 0)
  const taskGroupingSummary = computed(() => current.value?.task_grouping_summary ?? null)
  const isEmpty = computed(() => Object.keys(sections.value).length === 0)

  const notFoundCountBySection = computed<Record<string, number>>(() => {
    const counts: Record<string, number> = {}
    for (const ext of allExtractions.value) {
      counts[ext.section_key] = ext.fields.filter(f => f.completeness === 'not_found').length
    }
    return counts
  })

  const runningAssessmentId = computed<string | null>(() =>
    current.value?.running_assessment_id ?? null
  )

  const actionPlanTasks = computed(() =>
    allTasks.value.filter((t) => t.source_type === 'action_plan')
  )

  const consistencyTasks = computed(() =>
    allTasks.value.filter((t) => t.source_type === 'consistency')
  )

  const continuityTasks = computed(() =>
    allTasks.value.filter((t) => t.source_type === 'continuity')
  )

  // Tab badges count OPEN tasks = pending + auto-rejected (directive_rejected).
  // continuityTasksCount prefers the backend recompute (continuity_tasks_count)
  // and falls back to the client-side count below; totalConsistencyTaskCount is
  // client-side only. See MYAC-719.
  const isOpenTask = (t: Task) =>
    t.effectiveDecision === 'pending' || t.effectiveDecision === 'directive_rejected'

  const totalConsistencyTaskCount = computed(() =>
    consistencyTasks.value.filter(isOpenTask).length
  )

  const continuityTasksCount = computed(() =>
    current.value?.continuity_tasks_count ?? continuityTasks.value.filter(isOpenTask).length
  )

  const taskCountBySection = computed(() => {
    const counts = new Map<string, number>()
    for (const task of allTasks.value) {
      if (!task.section_key) continue
      counts.set(task.section_key, (counts.get(task.section_key) ?? 0) + 1)
    }
    return counts
  })

  function taskCountForSection(sectionKey: string): number {
    return taskCountBySection.value.get(sectionKey) ?? 0
  }

  const actionPlanTaskCountBySection = computed(() => {
    const counts = new Map<string, number>()
    for (const task of actionPlanTasks.value) {
      if (!task.section_key) continue
      counts.set(task.section_key, (counts.get(task.section_key) ?? 0) + 1)
    }
    return counts
  })

  function actionPlanTaskCountForSection(sectionKey: string): number {
    return actionPlanTaskCountBySection.value.get(sectionKey) ?? 0
  }

  const consistencyTaskCountBySection = computed(() => {
    const counts = new Map<string, number>()
    for (const task of consistencyTasks.value) {
      if (!task.section_key) continue
      counts.set(task.section_key, (counts.get(task.section_key) ?? 0) + 1)
    }
    return counts
  })

  function consistencyTaskCountForSection(sectionKey: string): number {
    return consistencyTaskCountBySection.value.get(sectionKey) ?? 0
  }

  const continuityTaskCountBySection = computed(() => {
    const counts = new Map<string, number>()
    for (const task of continuityTasks.value) {
      if (!task.section_key) continue
      counts.set(task.section_key, (counts.get(task.section_key) ?? 0) + 1)
    }
    return counts
  })

  function continuityTaskCountForSection(sectionKey: string): number {
    return continuityTaskCountBySection.value.get(sectionKey) ?? 0
  }

  const sectionsWithTasks = computed(() =>
    sectionEntries.value.filter((s) => taskCountForSection(s.key) > 0)
  )

  const sectionsWithActionPlanTasks = computed(() =>
    sectionEntries.value.filter((s) => actionPlanTaskCountForSection(s.key) > 0)
  )

  const sectionsWithConsistencyTasks = consistencyEntries

  function sectionByKey(key: string): (TDSectionSummary & { section_title: string; annex_ref?: string }) | null {
    const section = sections.value[key]
    if (!section) return null
    const found = tdSubsectionByKey(regulation.value, key)
    const title = found
      ? found.subsection.title
      : section.section_title ?? key
    const annex = found?.group.annex_ref ?? section.annex_ref
    return { ...section, section_title: title, annex_ref: annex }
  }

  /**
   * MYAC-822 — fetch the tenant's LATEST regulation-watch report (entries +
   * monitored regulations list) so the Applied Standards tab can compute its
   * labels live. Called on every tab activation; the tab gates its content on
   * `regWatchLiveLoading` so stale frozen labels are never shown.
   *
   * Mirrors the job's `_build_regulation_watch_lookup` data access: sibling
   * regulatory_monitoring project → regulation_reports ordered by generated_at
   * desc limit 1 → its entries. No sibling / no report is a graceful no-op
   * (empty inputs → every label resolves to null, header hidden).
   */
  async function resolveRegWatchLive(): Promise<void> {
    const seq = ++_regWatchSeq
    regWatchLiveLoading.value = true
    regWatchLiveError.value = false
    regWatchLive.value = null
    try {
      const projectsStore = useProjectsStore()
      await projectsStore.listProjects()
      const regPid =
        projectsStore.tenantProjects.find((p) => p.type === 'regulatory_monitoring')?.id ?? null
      let next: RegWatchLive = {
        entries: [], monitoredFulls: [], referenceDate: null, generatedDate: null,
      }
      if (regPid) {
        const firestore = useFirestore()
        const reportSnap = await getDocs(query(
          collection(firestore, `projects/${regPid}/regulation_reports`),
          orderBy('generated_at', 'desc'),
          limit(1),
        ))
        const reportDoc = reportSnap.docs[0]
        const report = reportDoc ? RegulationReport.fromFirebase(reportDoc) : null
        if (reportDoc && report) {
          const [entriesSnap, monitoredSnap] = await Promise.all([
            getDocs(collection(firestore, `projects/${regPid}/regulation_reports/${reportDoc.id}/entries`)),
            getDocs(collection(firestore, `projects/${regPid}/regulation_lists`)),
          ])
          next = {
            entries: entriesSnap.docs
              .map((d) => ReportEntry.fromFirebase(d))
              .filter((e): e is ReportEntry => e !== null)
              .map((e) => ({ designation: e.designation, category: e.category, badge: e.badge })),
            monitoredFulls: [...new Set(
              monitoredSnap.docs
                .map((d) => RegulationListItem.fromFirebase(d))
                .filter((r): r is RegulationListItem => r !== null)
                .map((r) => normFull(r.designation))
                .filter(Boolean),
            )],
            referenceDate: report.reference_date || null,
            generatedDate: report.generated_at?.toISOString().slice(0, 10) ?? null,
          }
        }
      }
      if (seq !== _regWatchSeq) return // a newer resolve superseded this one
      regWatchLive.value = next
    } catch (err) {
      logger.error('resolveRegWatchLive failed', err)
      if (seq === _regWatchSeq) regWatchLiveError.value = true
    } finally {
      if (seq === _regWatchSeq) regWatchLiveLoading.value = false
    }
  }

  return {
    current,
    loading,
    error,
    allTasks,
    allExtractions,
    appliedStandards,
    deviceAttributes,
    regWatchLive,
    regWatchLiveLoading,
    regWatchLiveError,
    resolveRegWatchLive,
    setCurrentProject,
    clear,
    retry,
    sections,
    sectionEntries,
    extractionEntries,
    groupLevelEntries,
    sectionsWithTasks,
    sectionsWithActionPlanTasks,
    sectionsWithConsistencyTasks,
    totalConsistencyTaskCount,
    continuityTasksCount,
    actionPlanTaskCountForSection,
    consistencyTaskCountForSection,
    continuityTaskCountForSection,
    macScore,
    macScorePrevious,
    macScoreProgression,
    overallRating,
    overallRatingCounts,
    aggregateTaskCounts,
    completenessCounts,
    consistencyCounts,
    complianceTasksCount,
    taskGroupingSummary,
    isEmpty,
    notFoundCountBySection,
    runningAssessmentId,
    sectionByKey,
    taskCountForSection,
    regulation,
    isContinuityApplicable,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTDCurrentStore, import.meta.hot))
}
